import React, { useState } from 'react';
import { Search, Columns, ChevronDown } from 'lucide-react';
import Pagination from './Pagination';

/**
 * 通用列表组件
 *
 * 带分页的表格卡片，完全照搬任务管理（TaskList）的UI结构。
 * 搜索/筛选/操作按钮由父组件在卡片外部渲染，本组件只负责表格+分页。
 *
 * Props:
 *   columns       - ColumnDef[]          — 列定义
 *   data          - array                — 行数据
 *   loading       - boolean              — 加载状态
 *   emptyMessage  - string               — 空数据提示
 *   rowKey        - string | (row) => any — 行唯一键
 *   page          - number               — 当前页（不传则不分页）
 *   totalItems    - number               — 总条数
 *   pageSize      - number               — 每页条数
 *   setPage       - (page) => void       — 翻页回调
 *   setPageSize   - (size) => void       — 切换每页条数回调
 *   onRowClick    - (row) => void        — 行点击回调
 *   searchPlaceholder - string           — 传了则显示搜索框
 *   searchTerm       - string            — 搜索框值
 *   onSearchChange   - (val) => void     — 搜索框值变化回调
 *   allColumns    - { key, label }[]     — 可选，传了则显示列配置按钮（列选择面板）
 *   className     - string               — 卡片容器额外类名
 */
const UniversalList = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = '',
  rowKey = 'id',
  page,
  totalItems,
  pageSize,
  setPage,
  setPageSize,
  onRowClick,
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  filterExtra,
  allColumns,
  loadingText = 'Loading...',
  className = '',
}) => {
  const getRowKey = (row) => {
    if (typeof rowKey === 'function') return rowKey(row);
    return row[rowKey];
  };

  const hasPagination = page !== undefined;
  const showSearch = searchPlaceholder !== undefined && searchPlaceholder !== null;
  const [visibleKeys, setVisibleKeys] = useState(
    allColumns ? allColumns.map(c => c.key) : null
  );
  const [colConfigOpen, setColConfigOpen] = useState(false);

  const displayColumns = visibleKeys
    ? columns.filter(c => visibleKeys.includes(c.key))
    : columns;

  return (
    <>
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        {showSearch && (
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={18} />
            <input
              type="text"
              value={searchTerm || ''}
              onChange={e => onSearchChange && onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white text-sm"
            />
          </div>
        )}
        {filterExtra}
        {allColumns && (
          <div className="relative">
            <button
              onClick={() => setColConfigOpen(!colConfigOpen)}
              className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white"
            >
              <Columns size={16} />
              Columns
              <ChevronDown size={14} />
            </button>
            {colConfigOpen && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] p-4 animate-in fade-in slide-in-from-top-2 overflow-visible">
                <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 mb-3">Display Fields</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {allColumns.map(col => (
                    <label key={col.key} className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={visibleKeys.includes(col.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVisibleKeys([...visibleKeys, col.key]);
                          } else {
                            setVisibleKeys(visibleKeys.filter(k => k !== col.key));
                          }
                        }}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-max">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-300 text-sm font-medium">
            <tr>
              {displayColumns.map(col => (
                <th
                  key={col.key}
                  className={`px-6 py-3 border-b border-slate-200 dark:border-slate-700 ${col.headerClassName || col.className || ''} ${col.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {loading ? (
              <tr>
                <td colSpan={displayColumns.length} className="px-6 py-10 text-center text-slate-400 dark:text-slate-500 italic">
                  {loadingText}
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map(row => (
                <tr
                  key={getRowKey(row)}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  {displayColumns.map(col => (
                    <td
                      key={col.key}
                      className={`px-6 py-4 dark:text-slate-300 ${col.className || ''} ${col.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                    >
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={displayColumns.length} className="px-6 py-10 text-center text-slate-400 dark:text-slate-500 italic">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasPagination && (
        <Pagination
          currentPage={page}
          totalItems={totalItems || 0}
          pageSize={pageSize || 20}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      )}
    </div>
    </>
  );
};

export default UniversalList;
